import * as React from 'react';
import { cva, type VariantProps } from 'class-variance-authority';
import { cn } from '@/lib/utils';

/**
 * Sober DS (v2) — Metric. Centered metric tile (.metric / .metric--* in the
 * showcase app.css). Big bold 28 value over a 12 secondary label, centered,
 * inside a single 1px outline. Variant supplies a subtle tinted background
 * (success/danger tint or the neutral app surface) — no colored borders.
 */
const metricVariants = cva(
  'flex flex-col items-center text-center p-5 rounded-so-lg border border-sober-outline shadow-so-card',
  {
    variants: {
      variant: {
        success: 'bg-sober-green-bg',
        danger: 'bg-sober-red-bg',
        neutral: 'bg-sober-app',
      },
    },
    defaultVariants: { variant: 'neutral' },
  }
);

export interface MetricProps
  extends React.HTMLAttributes<HTMLDivElement>,
    VariantProps<typeof metricVariants> {
  value: React.ReactNode;
  label: React.ReactNode;
}

export const Metric = React.forwardRef<HTMLDivElement, MetricProps>(
  ({ className, value, label, variant, ...props }, ref) => (
    <div ref={ref} className={cn(metricVariants({ variant }), className)} {...props}>
      <span className="text-so-28 font-bold text-sober-text">{value}</span>
      <span className="text-so-12 text-sober-secondary">{label}</span>
    </div>
  )
);
Metric.displayName = 'Metric';

export { metricVariants };
